/**
 * Encapsulates all solution-run action logic consumed by EsaPanel chips.
 *
 * Two-step flow:
 *  1. drafting  → handleReview   → sends converse with solution_run_context, closes modal,
 *                                  status = 'reviewing'
 *  2. reviewing → handleInitiate → sends [INITIATE_RUN], waits for metadata.task_id on
 *                                  that turn or any later turn, then SOLUTION_RUN_INITIATED
 *
 * handleKeepEditing  → reopens canvas overlay with current form data intact
 * handleCancel       → full teardown (SOLUTION_RUN_CANCELLED)
 *
 * Accepts `sendMessage` as a parameter (EsaPanel already holds the instance
 * returned by useEsaConverse) to avoid double-instantiating that hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSolutionRunStore } from '../../Canvas/store/solutionRunStore';
import { getMessageCache } from '../cache/messageCacheInstance';
import { shellEventBus } from '../../../shell/events/eventBus';
import type { SolutionRunState } from '../../Canvas/store/solutionRunStore';
import { waitForTaskIdAfterInitiate } from './waitForTaskIdAfterInitiate';

export type RunStatus = SolutionRunState['status'];

export interface SolutionRunActions {
  status: RunStatus;
  isInitiating: boolean;
  isReviewing: boolean;
  /**
   * Step 1 — send the run configuration to the ESA agent as a converse call
   * with `solution_run_context` in the context payload, close the modal, and
   * transition to 'reviewing'. Falls back to 'error' if the call fails.
   */
  handleReview: () => Promise<void>;
  /** Step 2 — send [INITIATE_RUN] and fire SOLUTION_RUN_INITIATED. */
  handleInitiate: () => Promise<void>;
  /** Reopen the canvas overlay with current data for further editing. */
  handleKeepEditing: () => void;
  /** Full cancel — tears down overlay, store, and ESA thread. */
  handleCancel: () => void;
}

export function useSolutionRunActions(
  sendMessage: (text: string, extraContext?: Record<string, unknown>) => Promise<void>,
): SolutionRunActions {
  const [status, setStatus] = useState<RunStatus>(
    () => getSolutionRunStore().getState().status,
  );
  const [isInitiating, setIsInitiating] = useState(
    () => getSolutionRunStore().getState().status === 'initiating',
  );
  const [isReviewing, setIsReviewing] = useState(
    () => getSolutionRunStore().getState().status === 'reviewing',
  );
  const initiateAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return getSolutionRunStore().subscribe(state => {
      setStatus(state.status);
      setIsInitiating(state.status === 'initiating');
      setIsReviewing(state.status === 'reviewing');
    });
  }, []);

  useEffect(() => {
    return () => {
      initiateAbortRef.current?.abort();
    };
  }, []);

  /**
   * Step 1: send run configuration to ESA agent, then close modal and mark
   * status as 'reviewing'. The converse call carries:
   *   - A human-readable opening message so the user sees a prompt in the thread.
   *   - `solution_run_context` in the context payload with the full snapshot so
   *     the agent can reason about the run without relying on message parsing.
   */
  const handleReview = useCallback(async (): Promise<void> => {
    const store = getSolutionRunStore().getState();
    const snapshot = store.getSnapshot();
    if (!snapshot) return;

    const reviewMessage =
      `Please review this solution run configuration and confirm it looks correct:\n\n` +
      `**Solution:** ${snapshot.solution.label}\n` +
      (snapshot.dataScope ? `**Data Scope:** ${snapshot.dataScope}\n` : '') +
      (snapshot.objective ? `**Objective:** ${snapshot.objective}\n` : '') +
      (snapshot.guardrails.length > 0
        ? `**Guardrails:**\n${snapshot.guardrails.map(g => `• ${g.text}`).join('\n')}\n`
        : '') +
      `\nLet me know if you'd like any adjustments before initiating.`;

    try {
      await sendMessage(reviewMessage, { solution_run_context: snapshot });
      // Close modal and transition to reviewing only after the backend confirms
      store.setReviewing();
      shellEventBus.dispatch({ type: 'SOLUTION_RUN_MODAL_CLOSE' });
    } catch {
      store.setError();
    }
  }, [sendMessage]);

  /** Step 2: initiate the run after ESA review is accepted. */
  const handleInitiate = useCallback(async (): Promise<void> => {
    const store = getSolutionRunStore().getState();
    if (!store.solution) return;

    initiateAbortRef.current?.abort();
    const abort = new AbortController();
    initiateAbortRef.current = abort;

    store.setInitiating();
    try {
      // Baseline: only consider messages from Initiate Run onward (not earlier turns).
      const convId = store.conversationId;
      const cache = getMessageCache();
      const fromIndex = convId ? cache.getSnapshot(convId).length : 0;

      await sendMessage('Initiate Run');

      // task_id may be on the Initiate Run reply or any subsequent turn in-thread.
      const taskId = convId
        ? await waitForTaskIdAfterInitiate(convId, fromIndex, { signal: abort.signal })
        : '';

      if (abort.signal.aborted) return;

      shellEventBus.dispatch({
        type: 'SOLUTION_RUN_INITIATED',
        payload: { runId: taskId, navTarget: null },
      });
    } catch {
      if (!abort.signal.aborted) {
        getSolutionRunStore().getState().setError();
      }
    }
  }, [sendMessage]);

  /** Reopen canvas overlay with current form data intact. */
  const handleKeepEditing = useCallback((): void => {
    shellEventBus.dispatch({ type: 'SOLUTION_RUN_REOPEN_MODAL' });
  }, []);

  /** Full teardown. */
  const handleCancel = useCallback((): void => {
    initiateAbortRef.current?.abort();
    shellEventBus.dispatch({ type: 'SOLUTION_RUN_CANCELLED' });
  }, []);

  return { status, isInitiating, isReviewing, handleReview, handleInitiate, handleKeepEditing, handleCancel };
}
